import { AppVisualState } from 'src/app/core/types/app-visual-state.type';
import { AppAccent } from 'src/app/core/types/app-accent.type';
import { AppVisualContextService } from '../../services/app-visual-context.service';
import { HostBinding, inject, Component, EventEmitter, Input, OnChanges, OnInit, Output, AfterContentInit, ElementRef, TemplateRef } from '@angular/core';
import { MiniCardButton } from 'src/app/core/models/minicardbutton.model';

@Component({
  selector: 'app-mini-card',
  templateUrl: './mini-card.component.html',
  styleUrl: './mini-card.component.css',
})
export class MiniCardComponent implements OnInit, OnChanges, AfterContentInit {
  @Input() accent?: AppAccent;
  private readonly visualContext = inject(AppVisualContextService);
  @HostBinding('class') get visualClass(): string {
    return this.accent === undefined ? '' : this.visualContext.classForInputs(this.accent);
  }

  @Input({ required: true }) title: string;
  @Input() status?: MiniCardStatus;
  @Input() titleRouterLink?: { link: string; queryParams?: { [name: string]: any } };
  @Input() headerButtons: MiniCardButton[] | null = [];
  @Input() titleIcon?: string; // Ícone opcional exibido antes do título
  @Input() titleIconState?: AppVisualState;
  @Input() titleIconTooltip?: string; // Tooltip do ícone do título
  // Aceita qualquer template no header
  @Input() headerContent?: TemplateRef<any>;
  @Input() headerContentWidth: 'default' | 'compact' = 'default';
  get headerContentClass(): string {
    return `header-content-width-${this.headerContentWidth === 'compact' ? 45 : 60}`;
  }

  get statusClass(): string { return this.visualContext.classForState(this.status?.state); }
  get titleIconClass(): string { return this.visualContext.classForState(this.titleIconState); }

  hasContent = true;

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.validateHeaderButtons();
  }

  ngOnChanges(): void {
    this.validateHeaderButtons();
  }

  ngAfterContentInit(): void {
    setTimeout(() => {
      const contentElement = this.elementRef.nativeElement.querySelector('.card-body');
      if (contentElement) {
        const hasNonEmptyContent = Array.from(contentElement.childNodes).some((node: any) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent?.trim().length > 0;
          }
          if (node.nodeType === Node.ELEMENT_NODE) {
            return true;
          }
          return false;
        });
        this.hasContent = hasNonEmptyContent;
      } else {
        this.hasContent = false;
      }
    });
  }

  private validateHeaderButtons(): void {
    const typesParamsLink = ['number', 'string', 'boolean'];
    this.headerButtons = this.headerButtons.filter(button => button); // Remove elementos null ou undefined
    this.headerButtons.forEach(button => {
      if (button.callBack && button.routerLink) {
        throw new Error('callBack e routerLink não podem ser utilizados simultaneamente!');
      }
      if (button.routerLink && button.params) {
        let paramKeys = Object.keys(button.params);
        paramKeys.forEach(key => {
          let paramType = typeof button.params[key];
          if (button.params[key] == null) return;
          if (!typesParamsLink.includes(paramType)) {
            throw new Error(`Tipo de parâmetro '${key}' não é suportado para um routerLink. Os tipos suportados são: ${typesParamsLink.join(', ')}.`);
          }
        });
      }
    });
  }

  runCallBack(button: MiniCardButton) {
    if (typeof button.callBack !== 'function') return;
    button.params ? button.callBack(...Object.values(button.params)) : button.callBack();
  }

  protected trackByIndex(index: number): number {
    return index;
  }
}

export interface MiniCardStatus {
  text: string;
  state?: AppVisualState;
  icon?: string;
  tooltip?: string;
  link?: MiniCardStatusTarget;
  options?: (MiniCardStatusTarget & { text: string })[];
}

interface MiniCardStatusTarget {
  routerLink: string;
  queryParams?: { [name: string]: string | number };
}
